import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { faLock } from "@fortawesome/pro-regular-svg-icons";

import { TicketAttachmentMeta, TicketComment } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { ticketCommentAuthorName } from "@/pages/Private/pages/Tickets/utils/staffTicketRowMeta";
import { classNames } from "@/utils/classNames";

import { ticketUserFirstName, ticketUserInitials } from "../utils/ticketThread";
import { TicketAttachmentChip } from "./TicketAttachmentChip";

interface TicketMessageProps {
	comment: TicketComment;
	isStaff: boolean;
	/**
	 * An internal note (PREC-3940 decision 9): amber bubble and the lock tag. Only the staff thread
	 * passes it; the customer thread never has such a row.
	 */
	isInternal?: boolean;
	/** Extra files shown under this bubble — the ticket's body-level files ride on the first message. */
	extraAttachments?: TicketAttachmentMeta[];
	onAttachmentError: (fileName: string) => void;
}

export const TICKET_DATE_FORMAT = "MMM d, HH:mm";

const bubbleClass = (isStaff: boolean, isInternal: boolean): string => {
	if (isInternal) {
		return "border-status-warning-light bg-status-running-light";
	}

	return isStaff
		? "border-status-success-light bg-status-success-light"
		: "border-gray-200 bg-white";
};

/**
 * One MESSAGE bubble. Staff get the green avatar and the "Signalist Support" tag (mockup
 * `.msg.staff`); an internal note the amber bubble and the lock tag (`.msg.internal`, PREC-3940).
 */
export const TicketMessage: FunctionComponent<TicketMessageProps> = ({
	comment,
	isStaff,
	isInternal = false,
	extraAttachments = [],
	onAttachmentError,
}) => {
	const { t } = useTranslation();
	const attachments = [...extraAttachments, ...comment.attachments];
	const initials = ticketUserInitials(comment.author);

	return (
		<div className="flex gap-3">
			<span
				className={classNames(
					"flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
					isStaff
						? "bg-status-success-light text-status-success"
						: "bg-blue-action-200 text-brand-light"
				)}
			>
				{isStaff ? initials[0] : initials}
			</span>
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<div className="flex items-baseline gap-2 text-xs">
					<span className="font-semibold text-text">
						{isStaff ? ticketUserFirstName(comment.author) : ticketCommentAuthorName(comment)}
					</span>
					{isStaff && (
						<span className="rounded-md bg-status-success-light px-1.5 py-0.5 text-[11px] font-medium text-status-success">
							{t("support.detail.thread.staffRole")}
						</span>
					)}
					<span className="text-text-meta">
						{format(new Date(comment.createdAt), TICKET_DATE_FORMAT)}
					</span>
				</div>
				<div
					className={classNames(
						"rounded-xl border px-3.5 py-3 text-sm text-text",
						bubbleClass(isStaff, isInternal)
					)}
				>
					{isInternal && (
						<span className="mb-1.5 inline-flex items-center gap-1 rounded-md bg-status-warning-light px-1.5 py-0.5 text-[11px] font-semibold text-status-wrong-person">
							<Icon icon={faLock} />
							{t("support.staff.detail.thread.internalNote")}
						</span>
					)}
					<p className="whitespace-pre-wrap">{comment.body}</p>
					{attachments.length > 0 && (
						<div className="mt-2.5 flex flex-wrap gap-2">
							{attachments.map((attachment) => (
								<TicketAttachmentChip
									key={attachment.id}
									attachment={attachment}
									thumbnail
									onError={onAttachmentError}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
